import {Link, makeStyles} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import {grey} from "@material-ui/core/colors";
import Typography from "@material-ui/core/Typography";
import groupBy from "lodash.groupby";
import React, {FC} from "react";
import {Link as ScrollLink} from "react-scroll";
import {ICategory, IGlossary, ITerm} from "@evennt-tech/api-v2-core";

export type AlphabeticFilterProps = {
  terms: ITerm[];
  categories: ICategory[];
  /**
   * Optional because nothing here reads it: the index is built purely from
   * `terms` and `categories`. Kept in the signature for the Show-level callers
   * that already pass it, and left optional so the library preview — which has
   * no Show and therefore no glossary — can reuse this component as it is.
   */
  glossary?: IGlossary;
  containerId: string;
  isPrintView?: boolean;
};

const AlphabeticFilter: FC<AlphabeticFilterProps> = ({
  terms,
  categories,
  containerId,
  isPrintView,
}) => {
  const classes = useStyles();
  const grouped = Object.entries(
    groupBy(terms, ({title}) => title.charAt(0).toUpperCase())
  );

  const renderGroupItems = (values: ITerm[]) =>
    values.map(({title, categoryId, id}) => {
      const category = categories.find(
        (categoryItem) => categoryItem.id === categoryId
      );
      const sectionId = `${category?.id}_${id}`;

      if (isPrintView) {
        return (
          <Link key={id} className={classes.value} href={`#${sectionId}`}>
            {title}
          </Link>
        );
      }

      return (
        <ScrollLink
          key={id}
          className={classes.value}
          to={sectionId}
          smooth
          duration={500}
          containerId={containerId}
        >
          {title}
        </ScrollLink>
      );
    });

  const renderGroup = () =>
    grouped.map(([letter, values]) => (
      <Box key={letter} display="flex" className={classes.group}>
        <Typography className={classes.letter}>{letter}</Typography>
        <Box
          display="flex"
          flexDirection="column"
          style={{flex: 1}}
          className={classes.items}
        >
          {renderGroupItems(values)}
        </Box>
      </Box>
    ));

  return <Box className={classes.alphabeticFilter}>{renderGroup()}</Box>;
};

const useStyles = makeStyles((theme) => ({
  alphabeticFilter: {
    columns: 3,
    marginBottom: theme.spacing(3),
  },
  group: {
    marginBottom: theme.spacing(6),
    "-webkit-column-break-inside": "avoid",
    "page-break-inside": "avoid",
    breakInside: "avoid",
  },
  letter: {
    width: theme.spacing(3),
    flexShrink: 0,
    color: grey[500],
    fontSize: 14,
    lineHeight: "100%",
  },
  items: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "calc(100% - 50px)",
    maxWidth: "calc(100% - 50px)",
  },
  value: {
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    fontSize: theme.spacing(1.8),
    cursor: "pointer",
    textDecoration: "underline",
    color: "black",
    lineHeight: `${theme.spacing(2.4)}px`,

    "&:not(:first-child)": {
      marginTop: theme.spacing(2),
    },
  },
}));

export default AlphabeticFilter;
